import React, { useMemo, useState } from "react";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Lock } from "lucide-react";
import { brand, chartColors, neutral } from "../../theme/palette";
import { formatINR } from "../../utils/formatINR";
import { formatCompactINR, formatNumber, transactionBreakdown } from "../../utils/analyticsMetrics";
import { ChartCard, ChartTooltip, SegmentedControl, axisProps } from "./AnalyticsUI";

// same labels as the Payment Transactions page
const TYPE_LABELS = {
  wallet_topup: "Wallet Topup",
  charging: "Charging",
  transfer: "Transfer",
  qr_topup: "QR Topup",
  refund: "Refund",
  vehicle_to_wallet: "Vehicle to Wallet",
  station_to_wallet: "Station to Wallet",
  cashback: "Cashback",
};

const TYPE_COLORS = {
  charging: brand[600],
  refund: chartColors.danger,
  qr_topup: "#db2777",
  transfer: "#7c3aed",
  wallet_topup: brand[300],
  vehicle_to_wallet: "#0f766e",
  station_to_wallet: neutral[500],
  cashback: chartColors.warning,
};

const STATUS_COLORS = {
  completed: "#16a34a",
  captured: brand[600],
  pending: chartColors.warning,
  failed: chartColors.danger,
};

const label = (type) => TYPE_LABELS[type] || type;

const TransactionBreakdown = ({ isAdmin, transactions, isLoading, error, stationFiltered }) => {
  const [measure, setMeasure] = useState("count");
  const data = useMemo(() => transactionBreakdown(transactions), [transactions]);
  const typeRows = useMemo(
    () =>
      data.byType
        .map((t) => ({ ...t, name: label(t.type), color: TYPE_COLORS[t.type] || neutral[400] }))
        .sort((a, b) => (measure === "count" ? b.count - a.count : b.amount - a.amount)),
    [data.byType, measure]
  );
  const failed = data.byStatus.find((s) => s.status === "failed")?.count || 0;

  if (!isAdmin) {
    return (
      <div className="card flex items-center gap-3 p-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
          <Lock className="h-4 w-4" />
        </div>
        <p className="text-13 text-neutral-600">Transaction analytics are available to platform administrators.</p>
      </div>
    );
  }

  const empty = !transactions?.length;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <ChartCard
        className="lg:col-span-2"
        title="Transactions by type"
        description={`All wallet and payment transactions, not only charging. ${stationFiltered ? "Station filter applied: only transactions linked to that station are counted." : ""}`}
        actions={
          <SegmentedControl
            options={[{ value: "count", label: "Count" }, { value: "amount", label: "Amount" }]}
            value={measure}
            onChange={setMeasure}
          />
        }
        isLoading={isLoading}
        error={error}
        isEmpty={empty}
        height={Math.max(220, typeRows.length * 38)}
      >
        <ResponsiveContainer width="100%" height={Math.max(220, typeRows.length * 38)}>
          <BarChart data={typeRows} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" {...axisProps} width={120} tick={{ ...axisProps.tick, fontSize: 12, fill: neutral[700] }} />
            <Tooltip
              cursor={{ fill: neutral[100] }}
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="rounded-lg border border-line bg-white px-3 py-2.5 text-13 shadow-lg">
                    <p className="mb-1 text-xs font-medium text-muted">{payload[0].payload.name}</p>
                    <p className="tabular-nums"><span className="text-neutral-600">Transactions </span><span className="font-semibold">{formatNumber(payload[0].payload.count)}</span></p>
                    <p className="tabular-nums"><span className="text-neutral-600">Amount </span><span className="font-semibold">{formatINR(payload[0].payload.amount)}</span></p>
                    <p className="tabular-nums"><span className="text-neutral-600">Settled </span><span className="font-semibold">{formatINR(payload[0].payload.settledAmount)}</span></p>
                  </div>
                ) : null
              }
            />
            <Bar dataKey={measure} radius={[0, 3, 3, 0]} maxBarSize={22} label={{ position: "right", fontSize: 11, fill: neutral[600], formatter: measure === "count" ? formatNumber : formatCompactINR }}>
              {typeRows.map((t) => <Cell key={t.type} fill={t.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Transaction status"
        description="Outcome of all transactions in the period."
        isLoading={isLoading}
        error={error}
        isEmpty={empty}
      >
        <ResponsiveContainer width="100%" height={170}>
          <PieChart>
            <Pie data={data.byStatus} dataKey="count" nameKey="status" innerRadius={52} outerRadius={74} paddingAngle={2} stroke="none" startAngle={90} endAngle={-270}>
              {data.byStatus.map((s) => <Cell key={s.status} fill={STATUS_COLORS[s.status] || neutral[400]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip formatters={{ count: formatNumber }} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="mt-2 space-y-2">
          {data.byStatus.map((s) => (
            <div key={s.status} className="flex items-center justify-between text-13">
              <span className="flex items-center gap-2 capitalize text-neutral-600">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLORS[s.status] || neutral[400] }} />
                {s.status}
              </span>
              <span className="tabular-nums font-medium text-fg">
                {formatNumber(s.count)}
                <span className="ml-1.5 text-muted">{((s.count / data.total) * 100).toFixed(1)}%</span>
              </span>
            </div>
          ))}
        </div>
        {failed > 0 && (
          <p className="mt-3 text-xs text-muted">
            Failure rate: <span className="font-semibold text-red-700">{((failed / data.total) * 100).toFixed(1)}%</span> of transactions
          </p>
        )}
      </ChartCard>
    </div>
  );
};

export default TransactionBreakdown;
